// The run dialog's layout, pure (activity.selftest.ts): which settings are
// shown open (required, or with no default: what the operator must decide)
// and which are folded under "Settings" with a one-line summary of their
// current values ("SPY +11 more · Horizon: 21 · Start: 1 Jan 2015"),
// readable labels for bar-interval choices ("5m" → "5-minute bars"), and
// when a text setting names where the data is, so it is offered as a
// choice of the connected sources.

import type { ParameterSpec } from "@/api/workflows";
import { formatDate, toDate } from "@/lib/format";

import type { RunForm, RunFormValue } from "../model";
import { listWords } from "./runFilters";

const empty = (v: unknown) => v == null || v === "" || (Array.isArray(v) && v.length === 0);

/** Open: required, or without a default. Folded: optional with a default. */
export function runFormGroups(params: readonly ParameterSpec[]): { open: ParameterSpec[]; folded: ParameterSpec[] } {
  const open: ParameterSpec[] = [];
  const folded: ParameterSpec[] = [];
  for (const p of params) (p.required || empty(p.default) ? open : folded).push(p);
  return { open, folded };
}

/** "Horizon (days)" → "Horizon": the label without its bracketed unit. */
const labelBase = (p: ParameterSpec) => (p.label || p.name).replace(/\s*\([^)]*\)\s*$/, "").trim();

// ─── choices ───

const BAR = /^(\d+)(m|h|d)$/;
const BAR_UNIT: Record<string, [string, string]> = { m: ["minute", "Minute"], h: ["hour", "Hourly"], d: ["day", "Daily"] };

/** "5m" → "5-minute bars", "1h" → "Hourly bars", "1d" → "Daily bars"; null for anything else. */
export function barIntervalLabel(value: string): string | null {
  const m = BAR.exec(value.trim());
  if (!m) return null;
  const [, n, unit] = m;
  return n === "1" ? `${BAR_UNIT[unit][1]} bars` : `${n}-${BAR_UNIT[unit][0]} bars`;
}

/** A choice as the operator reads it: bar intervals spelled out when every choice is one. */
export function choiceLabel(choices: readonly string[], value: string): string {
  if (choices.length && choices.every((c) => BAR.test(c))) return barIntervalLabel(value) ?? value;
  return value;
}

// ─── where the data is ───

export const MARKET_DATA = "market-data";
export const MARKET_DATA_LABEL = "Market data (daily prices from the estate feed)";

/**
 * A free-text setting whose value (or default) is the market-data feed or a
 * connected source's name: offered as a choice of sources instead of a box
 * that expects an internal name.
 */
export function isSourceParam(p: ParameterSpec, value: RunFormValue | undefined, sourceNames: readonly string[]): boolean {
  if (p.type !== "string" || p.choices?.length) return false;
  const names = new Set([MARKET_DATA, ...sourceNames]);
  return (typeof value === "string" && names.has(value)) || (typeof p.default === "string" && names.has(p.default));
}

// ─── the summaries ───

function valueWords(p: ParameterSpec, v: RunFormValue | undefined, sourceTitle: (name: string) => string | null): string {
  if (v === undefined || v === "" || v === false || (Array.isArray(v) && v.length === 0)) return "";
  if (Array.isArray(v)) return listWords(v);
  if (v === true) return labelBase(p);
  if (p.type === "date") {
    const d = toDate(v);
    return `${labelBase(p)}: ${d ? formatDate(d, { weekday: false, year: "always" }) : v}`;
  }
  if (p.type === "string" && v === MARKET_DATA) return "Market data";
  const source = p.type === "string" ? sourceTitle(v) : null;
  if (source) return source;
  const shown = p.choices?.length ? choiceLabel(p.choices, v) : v;
  return `${labelBase(p)}: ${shown.length > 24 ? `${shown.slice(0, 23).trimEnd()}…` : shown}`;
}

/** The folded settings' current values, at most three parts: "SPY +11 more · Horizon: 21 · Start: 1 Jan 2015". */
export function settingsSummary(params: readonly ParameterSpec[], form: RunForm, sourceTitle: (name: string) => string | null = () => null): string {
  return params
    .map((p) => valueWords(p, form[p.name], sourceTitle))
    .filter(Boolean)
    .slice(0, 3)
    .join(" · ");
}

/** An optional date with no default, whose description says nothing: blank means today. */
export function paramHelp(p: ParameterSpec): string {
  if (p.description?.trim()) return p.description.trim();
  return p.type === "date" && !p.required && empty(p.default) ? "Leave blank for today." : "";
}
